import { useCallback, useMemo } from 'react';

import { useStoreContext } from 'features/store';

import { useLoyaltyServiceRewardsQuery } from 'generated/graphql-gateway';
import { useLoyaltyUserId } from 'state/loyalty/hooks/use-loyalty-user';
import { useServiceModeLive } from 'state/service-mode';

import { mapServiceRewardCategories } from '../utils';
import { getLoyaltyServiceRewardServiceMode } from '../utils/service-mode';

export function useLoyaltyServiceRewards({
  rewardIds,
  skip = false,
}: { rewardIds?: string[]; skip?: boolean } = {}) {
  const serviceMode = useServiceModeLive();
  const { store } = useStoreContext();
  const storeId = store?.number;
  const { loyaltyUserId, loading: loyaltyUserIdLoading } = useLoyaltyUserId();

  const loyaltyServiceMode = getLoyaltyServiceRewardServiceMode(serviceMode);
  const querySkip = skip || loyaltyUserIdLoading;
  const variables = loyaltyUserId
    ? {
        serviceMode: loyaltyServiceMode,
        storeId,
        omitInvalids: false,
        ...(rewardIds?.length ? { rewardIds } : {}),
      }
    : { storeId, omitInvalids: false, ...(rewardIds?.length ? { rewardIds } : {}) };

  const {
    data: loyaltyServiceRewardsData,
    error: loyaltyServiceRewardsError,
    loading: loyaltyServiceRewardsLoading,
    refetch: refetchLoyaltyServiceRewards,
  } = useLoyaltyServiceRewardsQuery({
    variables,
    fetchPolicy: 'network-only',
    errorPolicy: 'all',
    skip: querySkip,
  });

  const loading = loyaltyUserIdLoading || loyaltyServiceRewardsLoading;

  const categories = useMemo(
    () => loyaltyServiceRewardsData?.loyaltyServiceRewards?.categories ?? [],
    [loyaltyServiceRewardsData]
  );

  const {
    categories: rewardCategories,
    sanityRewardsMap,
    engineRewardsMap,
    rewards,
    rewardsMap,
  } = useMemo(() => mapServiceRewardCategories(categories), [categories]);

  const refreshRewards = useCallback(async () => {
    if (skip || loyaltyUserIdLoading) {
      return;
    }
    await refetchLoyaltyServiceRewards();
  }, [loyaltyUserIdLoading, refetchLoyaltyServiceRewards, skip]);

  return {
    rewardCategories,
    rewards,
    rewardsMap,
    sanityRewardsMap,
    engineRewardsMap,
    error: loyaltyServiceRewardsError,
    loading,
    refreshRewards,
  };
}
